import React, { useCallback, useEffect, useState } from "react";
import "./DynamicGrid.css";

import Box from "@mui/material/Box";
import { alpha, styled } from "@mui/material/styles";
import {
  DataGrid,
  GridColDef,
  gridClasses,
  GridSortDirection,
  GridFeatureMode,
  GridPaginationModel,
  GridRenderCellParams,
  GridRowSelectionModel,
  GridRowModel,
} from "@mui/x-data-grid";
import PhotoLibraryOutlinedIcon from "@mui/icons-material/PhotoLibraryOutlined";
import LaunchIcon from "@mui/icons-material/Launch";
import Button from "@mui/material/Button";
import { Tooltip } from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  APP,
  AppURLs,
  DATATABLE_VIEW_EDIT_URLS,
  EOsdType,
  ETab,
  TAB_TO_GRID_HEADING_MAP,
  TGridSortModel,
  TPageObject,
} from "../../constants";
import { IProImage } from "../../models/master";
import { PopoutNotification } from "../../AppState/popoutNotification";
import NOTIFICATION_TYPE from "../../AppState/NotificationType";
import { getProImages } from "../../API/getProImages";
import AssignTo from "../assignTo/AssignTo";
import Loader from "../loader/loader";
import DialogImage from "../popupImage/popupImage";
import {
  saveClearedPageObject,
  saveClearedSortObject,
  saveDamagesPageObject,
  saveDamagesSortObject,
  saveMisdeliveryPageObject,
  saveMisdeliverySortObject,
  saveMyAssignmentPageObject,
  saveMyAssignmentSortObject,
  saveOnHandDeliveryPageObject,
  saveOnHandDeliverySortObject,
  saveOverageListPageObject,
  saveOverageListSortObject,
  saveSearchExceptionPageObject,
  saveSearchExceptionSortObject,
  saveShortagesPageObject,
  saveShortagesSortObject,
} from "../../AppState/DataTableAction";
import get from "lodash/get";
import first from "lodash/first";
import {
  IFilterForm,
  TTerminal,
} from "../../AppState/overagesFilterFormActions";
import { EManifestType } from "../../models/manifest";
import {
  pageSelector,
  filterFromSelector,
  tabPositionSelector,
  currentTerminalSelector,
  sortSelector,
} from "../../AppState/selectors";
import { NoRowsOverlay } from "../NoRowsOverlay";
import ExportToExcel from "../exportToExcel";
import { UpdateOverageExceptionLocationAPI } from "../../API/updateOverageExceptionLocation";
import PhotoModal from "../photoModal/PhotoModal";
import CustomDialog from "../confirmationPopup/confirmationPopup";
import { useApiHeaders } from "../../@hooks/useApiHeaders";

const ODD_OPACITY = 0.2;

const StripedDataGrid = styled(DataGrid)(({ theme }) => ({
  [`& .${gridClasses.row}.even`]: {
    backgroundColor: theme.palette.grey[200],
    "&:hover, &.Mui-hovered": {
      backgroundColor: alpha(theme.palette.primary.main, ODD_OPACITY),
      "@media (hover: none)": {
        backgroundColor: "transparent",
      },
    },
  },
}));

// Extracted component renderers outside parent component
const OsdNumberCellRenderer: React.FC<{
  osdNumber: string;
  osdType: string;
  viewExceptionUrl: (osdNumber: string, type: string) => string;
}> = ({ osdNumber, osdType, viewExceptionUrl }) => (
  <Link to={viewExceptionUrl(osdNumber, osdType)}>
    <Tooltip title={osdNumber}>
      <span> {osdNumber}</span>
    </Tooltip>
  </Link>
);

const PhotosCellRenderer: React.FC<{
  row: any;
  onPhotoClick: (
    osdNumber: string,
    proNumbers: number[] | undefined,
    osdType: string,
  ) => void;
}> = ({ row, onPhotoClick }) => (
  <div>
    <PhotoLibraryOutlinedIcon
      data-testid="photo-icon"
      className="blue-color"
      onClick={() =>
        onPhotoClick(
          row.osdNumber,
          [
            row.proNumber,
            row.misdeliveredPalletPro,
            row.misdeliveredBolPro,
            row.od400ProNumber,
          ],
          row.osdType,
        )
      }
      to={""}
    />
  </div>
);

const ManifestCellRenderer: React.FC<{
  value: any;
  manifestType: string;
}> = ({ value, manifestType }) =>
  value ? (
    <Link
      to={`${AppURLs.manifestDetails.replace(":id", value)}?type=${manifestType}`}
    >
      <Tooltip title={value}>
        <span> {value}</span>
      </Tooltip>
    </Link>
  ) : (
    <span> {value}</span>
  );

const ViewDescriptionCellRenderer: React.FC<{
  osdNumber: string;
  osdType: string;
  cleared?: string;
}> = ({ osdNumber, osdType, cleared }) => {
  if (osdType === EOsdType.msd || cleared) return null;

  return (
    <Link to={AppURLs.viewDescription.replace(":osdNumber", osdNumber)}>
      <LaunchIcon className="blue-color" />
    </Link>
  );
};

// Render cell functions outside component to avoid recreation on each render
const createOsdNumberRenderer =
  (viewExceptionUrl: (osdNumber: string, type: string) => string) =>
  (params: GridRenderCellParams) => (
    <OsdNumberCellRenderer
      osdNumber={params.row["osdNumber"]}
      osdType={params.row["osdType"]}
      viewExceptionUrl={viewExceptionUrl}
    />
  );

const createPhotosRenderer =
  (
    handlePhotoClick: (
      osdNumber: string,
      proNumbers: number[] | undefined,
      osdType: string,
    ) => void,
  ) =>
  (params: GridRenderCellParams) => (
    <PhotosCellRenderer row={params.row} onPhotoClick={handlePhotoClick} />
  );

const createManifestRenderer =
  (manifestType: string) => (params: GridRenderCellParams) => (
    <ManifestCellRenderer
      value={params?.row?.manifest}
      manifestType={manifestType}
    />
  );

const createViewDescriptionRenderer = () => (params: GridRenderCellParams) => (
  <ViewDescriptionCellRenderer
    osdNumber={params.row["osdNumber"]}
    osdType={params.row["osdType"]}
    cleared={params.row["cleared"]}
  />
);

/// grid props
type Props = {
  rows?: any[];
  columns?: GridColDef[];
  rowCount?: number;
  disableColumnMenu?: boolean;
  checkboxSelection?: boolean;
  hideFooter?: boolean;
  id?: string;
  sortingOrder?: GridSortDirection[];
  sortingMode?: GridFeatureMode;
  paginationMode?: GridFeatureMode;
  rowHeight?: number;
  loading?: boolean;
  columnVisibilityModel?: any;
  tab?: ETab;
  onSuccessFullyAssigned?: () => void;
  setExportTo?: (value: string) => void;
};

const Grid: React.FC<Props> = ({
  rows = [],
  columns,
  rowCount,
  id = "id",
  columnVisibilityModel,
  tab = ETab.overages,
  onSuccessFullyAssigned,
  setExportTo,
  checkboxSelection = true,
  hideFooter = false,
  disableColumnMenu = false,
  sortingOrder = ["asc", "desc"],
  sortingMode = "server",
  paginationMode = "server",
  rowHeight = 35,
  loading = false,
}: Props): JSX.Element => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Modify rows before rendering
  rows = rows.map(
    ({
      valueRange,
      manifest,
      lastManifest,
      od400ProNumber,
      proNumber,
      misdeliveredPalletPro,
      misdeliveredBolPro,
      pickupNumber,
      investigationStatus,
      cleared,
      ...rest
    }) => ({
      ...rest,
      valueRange,
      value1k: valueRange?.toString() === "3" ? rest.value1k : "", // Set value1k to blank if valueRange !== "3"
      manifest: manifest === 0 ? "" : manifest, // Set manifest to blank if manifest is 0
      lastManifest: lastManifest === 0 ? "" : lastManifest, // Set lastManifest to blank if lastManifest is 0
      od400ProNumber: od400ProNumber === 0 ? "" : od400ProNumber, // Set od400ProNumber to blank if od400ProNumber is 0
      proNumber: proNumber === 0 ? "" : proNumber, // Set proNumber to blank if proNumber is 0
      misdeliveredPalletPro:
        misdeliveredPalletPro === 0 ? "" : misdeliveredPalletPro, // Set misdeliveredPalletPro to blank if misdeliveredPalletPro is 0
      misdeliveredBolPro: misdeliveredBolPro === 0 ? "" : misdeliveredBolPro, // Set misdeliveredBolPro to blank if misdeliveredBolPro is 0
      pickupNumber: pickupNumber === 0 ? "" : pickupNumber, // Set pickupNumber to blank if pickupNumber is 0
      investigationStatus:
        tab === ETab.dashboard && cleared ? "Cleared" : investigationStatus, // Set investigationStatus to blank if cleared
      cleared,
    }),
  );

  const [proImages, setProImages] = useState<IProImage[]>([]);
  const [pageLoading, setPageLoading] = React.useState<boolean>(false);
  const [exceptionNumber, setExceptionNumber] = React.useState<string>("");
  const [isOpenAssignPop, setIsOpenAssignPop] = React.useState<boolean>(false);
  const [isOpenExportPop, setIsOpenExportPop] = React.useState<boolean>(false);
  const [selectedOSDIds, setSelectedOSDIds] = useState<GridRowSelectionModel>(
    [],
  );
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [isPhotoModalOpen, setIsPhotoModalOpen] = useState<boolean>(false);
  const [photoOsdNumber, setPhotoOsdNumber] = useState<number | null>(null);
  const [photoProNumber, setPhotoProNumber] = useState<number | undefined>(
    undefined,
  );
  const [selectedPro, setSelectedPro] = useState<number | undefined>(undefined);

  const [proNumbersOptions, setProNumbersOptions] = useState<number[]>([]);

  const handlePhotoModalClose = () => {
    setIsPhotoModalOpen(false);
    setPhotoOsdNumber(null);
    setPhotoProNumber(undefined);
  };

  const [keepNonExistentRowsSelected, setKeepNonExistentRowsSelected] =
    useState<boolean>(true);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const tabPosition = useSelector(tabPositionSelector);
  const pageObject: GridPaginationModel | undefined = useSelector(pageSelector);
  const sortModel = useSelector(sortSelector);
  const currentTerminalValue: TTerminal = useSelector(currentTerminalSelector);
  const filterParams: IFilterForm = useSelector(filterFromSelector);

  const heading: { [key: string]: string } = React.useMemo(
    () => TAB_TO_GRID_HEADING_MAP[tab] ?? {},
    [tab],
  );

  const rowCols: string[] = Object.keys(heading);
  const rowsPerPageOptions: number[] = APP.rowsPerPageOptions;

  const viewExceptionUrl = useCallback((osdNumber: string, type: string) => {
    const trimmedType = type?.trim();

    const urlTemplate = DATATABLE_VIEW_EDIT_URLS[trimmedType] ?? "";
    return urlTemplate ? urlTemplate.replace(":osdNumber", osdNumber) : "";
  }, []);

  //  while click on photos action button icon
  const apiHeaders = useApiHeaders({ sectionName: "dataTable" });

  const handleOpen = useCallback(
    (osdNumber: number | string, proNumbers?: number | string) => {
      if (osdNumber === "0" || proNumbers === "0") {
        dispatch(
          PopoutNotification(
            NOTIFICATION_TYPE.WARNING,
            "Selected number is not valid",
            Date.now().toString(),
          ),
        );
        return;
      }

      setPageLoading(true);
      let documentFilter = APP.dataTablePhotoViewTypes;
      const param: any = {
        documentFilter,
        osdNumber: osdNumber ? Number(osdNumber) : undefined,
        proNumbers: proNumbers ? [Number(proNumbers)] : undefined,
      };

      getProImages(param, apiHeaders)
        .then((response) => {
          const sortImages = get(response, "data", []).sort(
            (a: IProImage, b: IProImage) =>
              a.imageFilename?.localeCompare(b.imageFilename),
          );
          setProImages(sortImages);
          if (response?.data?.length > 0) {
            setIsOpen(!isOpen);
            setPageLoading(false);
          } else {
            setPageLoading(false);
            const message = proNumbers
              ? `No images available for the PRO# ${proNumbers}.`
              : `No images available for the OSD# ${osdNumber}.`;
            dispatch(
              PopoutNotification(
                NOTIFICATION_TYPE.INFO,
                message,
                Date.now().toString(),
              ),
            );
          }
        })
        .catch((error) => {
          setPageLoading(false);
          dispatch(
            PopoutNotification(
              NOTIFICATION_TYPE.ERROR,
              typeof error === "string" ? error : "Image API error",
              Date.now().toString(),
            ),
          );
          console.error(error);
        });
    },
    [dispatch, isOpen, apiHeaders],
  );

  // Handle photo click callback
  const handlePhotoClickInternal = useCallback(
    (
      osdNumber: string,
      proNumbers: number[] = [],
      osdType: string = EOsdType.msd,
    ) => {
      setExceptionNumber(osdNumber);

      if (osdType === EOsdType.msd) {
        const proLIst = proNumbers.filter(
          (p): p is number =>
            typeof p === "number" && p !== 0 && !Number.isNaN(p),
        );
        if (proLIst.length === 0) {
          setDialogOpen(true);
          return;
        }
        handlePhotoModalClose();
        setPhotoOsdNumber(Number(osdNumber));
        setProNumbersOptions(proLIst);
        setIsPhotoModalOpen(true);
        return;
      }

      handleOpen(osdNumber);
    },
    [handleOpen],
  );

  // Configure columns and add manually style and
  const minWidthMap: Record<string, number> = {
    View: 30,
    Value: 70,
    Photos: 70,
    "Pro #": 100,
    "Clearing Code": 100,
    Commodity: 155,
    Description: 200,
    "Trailer #": 90,
    "MSD Pro #": 100,
    "Assigned to": 90,
    "Assigned by": 90,
    "Entry date": 120,
    "Entry User": 90,
    "Pickup Date": 120,
    "New PRO #": 100,
    "Exception #": 100,
    "Value Range": 90,

    "OD400 Pro #": 100,
    "OD400 #": 100,
    "Pickup #": 100,
    "Shipper Name": 110,
    "Driver's Name": 100,
    "Consignee Name": 120,
    "Exception code": 120,
    "Current Location": 120,
    "Last Manifest #": 120,
    "Misdelivery Pro #": 120,
    "MSD Full Address": 130,
    "Origin terminal": 80,
    "Destination terminal": 80,
    "Destination/Pickup terminal": 130,
    "Pickup terminal": 100,
    "Reporting terminal": 80,
    "PRO Applied To": 120,

    "Investigation Status": 130,
    "Dock Worker's Name": 150,
    "Last confirmed manifest #": 155,
    "Manifest Short Reported On": 155,
  };

  // Columns that should NOT have row selection on click
  const nonSelectableColumns = new Set([
    "osdNumber",
    "photos",
    "lastManifest",
    "viewDescription",
  ]);
  const sortingDisabledColumns = React.useMemo(
    () => new Set(APP.overage.sortingDisabledColumns),
    [],
  );

  const defaultRenderCell = (params: GridRenderCellParams) => (
    <Tooltip title={params.value}>
      <span>{params.value}</span>
    </Tooltip>
  );

  // Handle cell click to select the row
  const handleCellClickForSelection = useCallback(
    (rowId: any) => {
      // Check if row is cleared (unselectable)
      const row = rows.find((r: any) => r[id] === rowId);
      if (row?.cleared) return;

      setSelectedOSDIds((prev) => {
        const selectedSet = new Set(prev);
        if (selectedSet.has(rowId)) {
          selectedSet.delete(rowId);
          return Array.from(selectedSet);
        }
        selectedSet.add(rowId);
        return Array.from(selectedSet);
      });
    },
    [rows, id],
  );

  // Selectable cell renderer for columns that should select row on click
  const selectableRenderCell = useCallback(
    (params: GridRenderCellParams) => {
      const value = params.value;
      const rowId = params.row[id];
      const hasValue = value !== null && value !== undefined && value !== "";

      if (!hasValue) {
        return <span>{value}</span>;
      }

      return (
        <Tooltip title={value} /* NOSONAR */>
          <span /* NOSONAR */
            style={{ cursor: "pointer" }} /* NOSONAR */
            onClick={() => handleCellClickForSelection(rowId)} /* NOSONAR */
          >
            {value}
          </span>
        </Tooltip>
      );
    },
    [id, handleCellClickForSelection],
  );

  const specialRenderers: Partial<
    Record<string, (params: GridRenderCellParams) => React.ReactNode>
  > = React.useMemo(
    () => ({
      osdNumber: createOsdNumberRenderer(viewExceptionUrl),
      photos: createPhotosRenderer(handlePhotoClickInternal),
      manifest: createManifestRenderer(
        EManifestType[heading["manifest"] as keyof typeof EManifestType],
      ),
      lastManifest: (params: GridRenderCellParams /** NOSONAR */) => (
        <ManifestCellRenderer
          value={params?.row?.lastManifest}
          manifestType={
            EManifestType[heading["lastManifest"] as keyof typeof EManifestType]
          }
        />
      ),
      viewDescription: createViewDescriptionRenderer(),
    }),
    [viewExceptionUrl, handlePhotoClickInternal, heading],
  );

  const cols: GridColDef[] =
    columns ??
    rowCols.map((ele) => {
      const headingValue = heading[ele];
      const minWidth = minWidthMap[headingValue];

      // Determine which renderer to use
      let renderCell: GridColDef["renderCell"];
      if (specialRenderers[ele]) {
        renderCell = specialRenderers[ele] as GridColDef["renderCell"];
      } else if (nonSelectableColumns.has(ele)) {
        renderCell = defaultRenderCell;
      } else {
        renderCell = selectableRenderCell;
      }

      return {
        field: ele,
        headerName: headingValue,
        description: headingValue,
        type: "string",
        editable: true,
        flex: 1,
        headerAlign: "left",
        align: "left",
        disableColumnMenu: true,
        renderCell,
        sortable: !sortingDisabledColumns.has(ele),
        ...(minWidth ? { minWidth } : {}),
      };
    });

  const effectiveColumnVisibilityModel = React.useMemo(() => {
    if (tabPosition !== ETab.dashboard) return columnVisibilityModel;
    return {
      ...columnVisibilityModel,
      cleared: false,
    };
  }, [tabPosition, columnVisibilityModel]);

  const unselectableRowIds = React.useMemo(() => {
    const ids = new Set<any>();
    rows.forEach((row: any) => {
      if (row?.cleared) {
        ids.add(row?.[id]);
      }
    });
    return ids;
  }, [rows, id]);

  // set selected row on variables
  const onRowsSelectionHandler = (osdIds: GridRowSelectionModel) => {
    // Do not allow selection for exceptions that already have a cleared.
    setSelectedOSDIds(osdIds.filter((rowId) => !unselectableRowIds.has(rowId)));
  };

  // navigate overage entry page, click on "Entry Overage" button.
  const navigateToOverageEntry = () => {
    if (tabPosition === ETab.shortages) {
      navigate(AppURLs.shortageEntry);
    } else if (tabPosition === ETab.onHandDelivery) {
      navigate(AppURLs.ohEntry);
    } else if (tabPosition === ETab.misdelivery) {
      navigate(AppURLs.mdEntry);
    } else {
      navigate(AppURLs.overageEntry);
    }
  };

  // store the sort object in redux store and update sort object state
  const onColumnSort = (sortObj: TGridSortModel[] | []) => {
    let sObj = structuredClone(APP.sortModel);
    const pageObj: GridPaginationModel = {
      pageSize: get(pageObject, "pageSize"),
      page: 0,
    };
    if (sortObj?.length > 0) {
      sObj = structuredClone(first(sortObj) as TGridSortModel);
    }
    setKeepNonExistentRowsSelected(false);

    /* tabPosition ETab.overages = 1 is for Overages, ETab.myAssignments = 2 is for My Assignment
     AND ETab.shortages = 3 for Shortages **/

    const dispatchMap = {
      [ETab.overages]: {
        sort: saveOverageListSortObject,
        page: saveOverageListPageObject,
      },
      [ETab.myAssignments]: {
        sort: saveMyAssignmentSortObject,
        page: saveMyAssignmentPageObject,
      },
      [ETab.shortages]: {
        sort: saveShortagesSortObject,
        page: saveShortagesPageObject,
      },
      [ETab.damages]: {
        sort: saveDamagesSortObject,
        page: saveDamagesPageObject,
      },
      [ETab.onHandDelivery]: {
        sort: saveOnHandDeliverySortObject,
        page: saveOnHandDeliveryPageObject,
      },
      [ETab.misdelivery]: {
        sort: saveMisdeliverySortObject, // Reuse onHandDelivery sort for now
        page: saveMisdeliveryPageObject, // Reuse onHandDelivery page for now
      },
      [ETab.dashboard]: {
        sort: saveSearchExceptionSortObject,
        page: saveSearchExceptionPageObject,
      },
      [ETab.cleared]: {
        sort: saveClearedSortObject,
        page: saveClearedPageObject,
      },
    } as const;

    const tabDispatchers = dispatchMap[tabPosition as keyof typeof dispatchMap]; // NOSONAR

    if (tabDispatchers) {
      dispatch(tabDispatchers.sort(sObj));
      dispatch(tabDispatchers.page(pageObj));
    }
  };

const onPageChange = (pageObj: TPageObject) => {
  const pageDispatchMap = {
    [ETab.overages]: saveOverageListPageObject,
    [ETab.myAssignments]: saveMyAssignmentPageObject,
    [ETab.shortages]: saveShortagesPageObject,
    [ETab.damages]: saveDamagesPageObject,
    [ETab.onHandDelivery]: saveOnHandDeliveryPageObject,
    [ETab.misdelivery]: saveMisdeliveryPageObject,
    [ETab.dashboard]: saveSearchExceptionPageObject,
    [ETab.cleared]: saveClearedPageObject,
  } as const;

  let page: TPageObject = structuredClone(pageObj);

  const isPageSizeChanged =
    get(pageObj, "pageSize") !== get(pageObject, "pageSize");

  if (isPageSizeChanged) {
    page = {
      pageSize: get(pageObj, "pageSize"),
      page: 0,
    };

    setKeepNonExistentRowsSelected(false);

    // Update all tabs
    Object.values(pageDispatchMap).forEach((pageDispatcher) => {
      dispatch(pageDispatcher(page));
    });
  } else {
    setKeepNonExistentRowsSelected(true);

    const pageDispatcher =
      pageDispatchMap[tabPosition as keyof typeof pageDispatchMap];

    if (pageDispatcher) {
      dispatch(pageDispatcher(page));
    }
  }
};

  // Open modal popup for Assign to other user except to him/her self
  const openAssignTo = () => {
    setIsOpenAssignPop(true);
  };

  const openExportTo = () => {
    setIsOpenExportPop(true);
  };

  //Close Assign to other popup
  const onClosePop = () => {
    setIsOpenAssignPop(false);
    setIsOpenExportPop(false);
  };

  //Close Assign to other popup on successfully assign and deselect row and fetch fresh data and stop loading
  const onSuccessFullyAssignedException = () => {
    setSelectedOSDIds([]);
    setIsOpenAssignPop(false);
    setKeepNonExistentRowsSelected(false);
    if (onSuccessFullyAssigned) onSuccessFullyAssigned();
  };

  const alphaNumRegex = /^[A-Za-z0-9]+$/;

  const validateExceptionLocation = (value: string): string | null => {
    if (!value) return "Current Location cannot be empty.";
    if (!alphaNumRegex.test(value))
      return "Current Location must be alphanumeric.";
    if (value.length > 15)
      return "Current Location cannot exceed 15 characters.";
    return null;
  };

  const handleRowUpdate = async (
    newRow: GridRowModel,
    oldRow: GridRowModel,
  ) => {
    const exceptionLocation = newRow.exceptionLocation?.toString().trim() ?? "";
    if (!exceptionLocation) return newRow; // Return early if value is empty to avoid unnecessary API call and validation error (as empty value is not allowed)
    const oldExceptionLocation =
      oldRow.exceptionLocation?.toString().trim() ?? "";

    const notify = (type: NOTIFICATION_TYPE, message: string) => {
      dispatch(PopoutNotification(type, message, Date.now().toString()));
    };

    const validationError = validateExceptionLocation(exceptionLocation);
    if (validationError) {
      notify(NOTIFICATION_TYPE.ERROR, validationError);
      return oldRow;
    }

    if (exceptionLocation !== oldExceptionLocation) {
      // ✅ Trigger loader *after* validation but let DataGrid keep newRow optimistically
      setPageLoading(true);
      try {
        await UpdateOverageExceptionLocationAPI({
          osdNumber: newRow.osdNumber,
          exceptionLocation,
        });
        notify(
          NOTIFICATION_TYPE.SUCCESS,
          "Current Location updated successfully.",
        );
        setPageLoading(false);
        if (onSuccessFullyAssigned) onSuccessFullyAssigned();
        return { ...newRow, exceptionLocation };
      } catch (error) {
        notify(
          NOTIFICATION_TYPE.ERROR,
          typeof error === "string"
            ? error
            : "Failed to update Current Location.",
        );
        console.error("Error updating current location:", error);
        setPageLoading(false);
        return newRow;
      }
    }

    return newRow;
  };

  // Deselect existent rows Selected on terminal and filter parameter changed
  useEffect(() => {
    setKeepNonExistentRowsSelected(false);
  }, [currentTerminalValue, filterParams]);

  useEffect(() => {
    if (photoProNumber) {
      setSelectedPro(photoProNumber);
      handlePhotoModalClose();
      handleOpen("", photoProNumber);
    }
  }, [photoProNumber, handleOpen]);

  return (
    <div
      data-testid="dynamic-grid-container"
      className={tabPosition === ETab.cleared ? "cleared-exception" : ""}
    >
      <Box className="grid-padding" data-testid="dynamic-grid-box">
        <div
          style={{ height: "100%", width: "100%" }}
          data-testid="dynamic-grid-table-wrapper"
        >
          <StripedDataGrid
            rowHeight={rowHeight}
            data-testid="dynamic-grid-table"
            rows={rows}
            getRowId={(row: any) => row[id]}
            columns={cols ?? columns!}
            disableColumnFilter
            checkboxSelection={
              checkboxSelection && tabPosition !== ETab.cleared
            }
            hideFooter={hideFooter}
            disableColumnMenu={disableColumnMenu}
            onSortModelChange={(model: any) => onColumnSort(model)}
            sortModel={[sortModel ?? APP.sortModel]}
            loading={loading}
            rowCount={rowCount}
            keepNonExistentRowsSelected={keepNonExistentRowsSelected}
            getRowClassName={(params: any) => {
              const indexClass =
                params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd";
              if (tabPosition === ETab.dashboard) {
                const clearedClass = params.row?.cleared
                  ? "row-cleared"
                  : "row-uncleared";
                return `${indexClass} ${clearedClass}`;
              }
              return indexClass;
            }}
            paginationModel={pageObject}
            onRowSelectionModelChange={(ids: GridRowSelectionModel) =>
              onRowsSelectionHandler(ids)
            }
            rowSelectionModel={selectedOSDIds}
            onPaginationModelChange={(newPage: any) => onPageChange(newPage)}
            sortingMode={sortingMode}
            paginationMode={paginationMode}
            pageSizeOptions={rowsPerPageOptions}
            sortingOrder={sortingOrder}
            columnVisibilityModel={effectiveColumnVisibilityModel}
            disableRowSelectionOnClick
            slots={{
              noRowsOverlay: NoRowsOverlay,
            }}
            isRowSelectable={(params) => !params.row?.cleared}
            isCellEditable={(params) =>
              params.field === "exceptionLocation" &&
              ["OV", "NB"].includes(params.row.osdType)
            }
            processRowUpdate={handleRowUpdate} // 🔹 handle edits
          />
        </div>
      </Box>

      {tabPosition === ETab.dashboard && (
        <Box className="clearing-status-legend" data-testid="clearing-status-legend">
          <span className="uncleared-box"></span>
          <span className="uncleared-text">Uncleared</span>
          <span className="cleared-box"></span>
          <span className="cleared-text">Cleared</span>
        </Box>
      )}

      <Box
        className="button-set-pagination"
        data-testid="dynamic-grid-pagination-box"
      >
        <div className="table-export" data-testid="dynamic-grid-action-bar">
          {[ETab.overages].includes(tabPosition) ? (
            <Button
              onClick={navigateToOverageEntry}
              variant="contained"
              className="entry"
              data-testid="overage-entry-button"
            >
              Overage Entry
            </Button>
          ) : null}

          {[ETab.shortages].includes(tabPosition) ? (
            <Button
              onClick={navigateToOverageEntry}
              variant="contained"
              className="entry"
              data-testid="all-short-entry-button"
            >
              All Short Entry
            </Button>
          ) : null}

          {[ETab.onHandDelivery].includes(tabPosition) ? (
            <Button
              onClick={navigateToOverageEntry}
              variant="contained"
              className="entry"
              data-testid="oh-entry-button"
            >
              OH Entry
            </Button>
          ) : null}

          {[ETab.misdelivery].includes(tabPosition) ? (
            <Button
              onClick={navigateToOverageEntry}
              variant="contained"
              className="entry"
              data-testid="msd-entry-button"
            >
              MSD Entry
            </Button>
          ) : null}
          {tabPosition !== ETab.cleared && (
            <Button
              variant="contained"
              className={`assign${selectedOSDIds?.length > 0 ? "" : " disable"}`}
              onClick={selectedOSDIds?.length ? openAssignTo : undefined}
              data-testid="assign-button"
            >
              Assign
            </Button>
          )}
          <Button
            variant="contained"
            data-testid="export-excel-button"
            className={`assign ml${rows?.length ? "" : " disable"}`}
            onClick={rows?.length ? openExportTo : undefined}
          >
            Export to Excel
          </Button>
        </div>
      </Box>
      <div data-testid="dynamic-grid-modals">
        <AssignTo
          selectedOSDIds={selectedOSDIds}
          onClose={onClosePop}
          isOpen={isOpenAssignPop}
          key={isOpenAssignPop ? "open" : "close"}
          onSuccessFullyAssigned={onSuccessFullyAssignedException}
          data-testid="assign-to-modal"
        />

        <ExportToExcel
          onClose={onClosePop}
          isOpen={isOpenExportPop}
          setExportTo={setExportTo}
          recordCount={rowCount}
          data-testid="export-to-excel-modal"
        />
        <DialogImage
          isDialogOpened={isOpen}
          handleCloseDialog={() => setIsOpen(false)}
          imageData={proImages}
          key={exceptionNumber + isOpen?.toString()}
          excNumber={exceptionNumber}
          proNumber={selectedPro}
          data-testid="dialog-image-modal"
        />
        <Loader
          showProgress={pageLoading}
          pageLoader={loading || pageLoading}
          data-testid="dynamic-grid-loader"
        />
        <PhotoModal
          isOpen={isPhotoModalOpen}
          key={photoOsdNumber + isPhotoModalOpen?.toString()}
          proNumbersOptions={proNumbersOptions}
          onClose={handlePhotoModalClose}
          osdNumber={photoOsdNumber ?? undefined}
          setPhotoProNumber={setPhotoProNumber}
        />
        <CustomDialog
          key={dialogOpen ? "open" : "close"}
          message={`<p> No PRO to view the images associated </p>`}
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          isConfirmation={false}
        />
      </div>
    </div>
  );
};

export default Grid;
